<template>
  <MainLayout>

    <!-- -- Hero ----------------------------------------------------------------- -->
    <section class="relative bg-gradient-hero py-7 lg:py-10 overflow-hidden">
      <div class="absolute inset-0 hex-pattern pointer-events-none"></div>
      <div class="absolute left-1/3 top-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px] pointer-events-none"></div>
      <!-- Medical cross decorations -->
      <div class="absolute right-24 top-12 text-white/5 text-[120px] font-bold select-none hidden lg:block leading-none">+</div>
      <div class="absolute left-16 bottom-12 text-white/4 text-[80px] font-bold select-none hidden lg:block leading-none">+</div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="flex justify-center mb-4 animate-fade-up" style="animation-delay:0.1s">
          <div class="flex items-center gap-3">
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
            <span class="text-secondary text-xs font-bold uppercase tracking-[0.2em]">Reach Out to Us</span>
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
          </div>
        </div>
        <h1 class="text-5xl sm:text-6xl font-black text-white mb-4 animate-fade-up" style="animation-delay:0.2s">
          Contact <span class="text-secondary">Us</span>
        </h1>
        <p class="text-white/60 text-base max-w-lg mx-auto mb-6 animate-fade-up" style="animation-delay:0.3s">
          Our team is ready to answer your questions, provide quotations, and support your healthcare facility's needs.
        </p>
        <nav class="flex items-center justify-center gap-2 text-sm text-white/50 animate-fade-up" style="animation-delay:0.4s">
          <RouterLink to="/" class="hover:text-secondary transition-colors duration-200">Home</RouterLink>
          <i class="fa-solid fa-chevron-right text-xs text-white/30"></i>
          <span class="text-white/80">Contact</span>
        </nav>
      </div>
    </section>

    <!-- -- Contact Info Cards --------------------------------------------------- -->
    <section class="py-12 bg-[#f7f5fb]">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid sm:grid-cols-3 gap-5">
          <div
            v-for="info in contactInfo"
            :key="info.label"
            class="bg-white rounded-2xl p-6 shadow-card hover:shadow-card-lg hover:-translate-y-1 transition-all duration-300 text-center"
            data-aos="fade-up"
            :data-aos-delay="info.delay"
          >
            <div class="w-14 h-14 bg-gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-md">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6 text-white" v-html="info.svgPath"></svg>
            </div>
            <h4 class="font-bold text-gray-900 text-sm uppercase tracking-wider mb-2">{{ info.label }}</h4>
            <component
              :is="info.href ? 'a' : 'p'"
              :href="info.href || undefined"
              :class="[
                'text-gray-600 text-sm leading-relaxed font-medium',
                info.href ? 'hover:text-primary transition-colors duration-200' : '',
              ]"
            >
              {{ info.value }}
            </component>
            <p v-if="info.sub" class="text-gray-400 text-xs mt-1">{{ info.sub }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- -- Map ------------------------------------------------------------------ -->
    <div class="w-full h-[400px] bg-gray-100 overflow-hidden">
      <iframe
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.7882712278!2d32.5501!3d0.3049!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x177dbb0000000001%3A0x1!2sRubaga%20Rd%2C%20Kampala%2C%20Uganda!5e0!3m2!1sen!2sug!4v1716000000000!5m2!1sen!2sug"
        width="100%"
        height="400"
        style="border:0"
        allowfullscreen=""
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
        title="Orel Medical Supplies - Rubaga Road, Butikiro Complex, Kampala"
      ></iframe>
    </div>

    <!-- -- Form + Get in Touch -------------------------------------------------- -->
    <section class="py-20 lg:py-28 bg-white overflow-hidden">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-12 xl:gap-16">

          <!-- Contact Form -->
          <div data-aos="fade-right">
            <div class="section-label">Send a Message</div>
            <h2 class="section-title mb-2">Get a Quote</h2>
            <p class="text-gray-500 text-sm mb-8">Fill in the form below and our team will respond within 24 hours.</p>

            <form @submit.prevent="submitForm" class="space-y-5">
              <div class="grid sm:grid-cols-2 gap-5">
                <div>
                  <label class="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Full Name</label>
                  <input
                    v-model="form.name"
                    type="text"
                    placeholder="Enter your name"
                    required
                    class="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200"
                  />
                </div>
                <div>
                  <label class="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Email Address</label>
                  <input
                    v-model="form.email"
                    type="email"
                    placeholder="Enter your email"
                    required
                    class="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200"
                  />
                </div>
              </div>

              <div class="grid sm:grid-cols-2 gap-5">
                <div>
                  <label class="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Subject</label>
                  <input
                    v-model="form.subject"
                    type="text"
                    placeholder="e.g. Equipment Quote, Training"
                    class="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200"
                  />
                </div>
                <div>
                  <label class="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Phone Number</label>
                  <input
                    v-model="form.phone"
                    type="tel"
                    placeholder="+256 7XX XXX XXX"
                    class="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200"
                  />
                </div>
              </div>

              <!-- Service interest -->
              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Service of Interest</label>
                <select
                  v-model="form.service"
                  class="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200"
                >
                  <option value="" disabled>Select a service...</option>
                  <option v-for="s in serviceOptions" :key="s" :value="s">{{ s }}</option>
                </select>
              </div>

              <div>
                <label class="block text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">Message</label>
                <textarea
                  v-model="form.message"
                  rows="5"
                  placeholder="Tell us about your facility's needs..."
                  required
                  class="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200 resize-none"
                ></textarea>
              </div>

              <!-- Success message -->
              <div v-if="submitted" class="flex items-center gap-3 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 text-green-500 flex-shrink-0">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Message sent! Our team will be in touch within 24 hours.
              </div>

              <button
                type="submit"
                :disabled="sending"
                class="btn-primary w-full justify-center py-4 text-base"
              >
                <svg v-if="!sending" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                  <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
                <i v-else class="fa-solid fa-spinner animate-spin text-sm"></i>
                {{ sending ? 'Sending...' : 'Send Message' }}
              </button>
            </form>
          </div>

          <!-- Get in Touch -->
          <div data-aos="fade-left" data-aos-delay="150">
            <div class="section-label">Get in Touch</div>
            <h2 class="section-title mb-4">
              Reach Orel Medical<br/>Supplies Ltd
            </h2>
            <p class="text-gray-500 leading-relaxed mb-8 text-justify">
              Ready to experience excellence in medical supplies and services? Contact us today to learn more about how Orel Medical Supplies Ltd can support your healthcare facility's mission.
            </p>

            <!-- Contact details -->
            <div class="space-y-5 mb-8">
              <div v-for="detail in details" :key="detail.label" class="flex items-start gap-4">
                <div class="w-12 h-12 bg-primary/8 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-primary" v-html="detail.svgPath"></svg>
                </div>
                <div>
                  <h5 class="font-bold text-gray-900 text-sm mb-0.5">{{ detail.label }}</h5>
                  <component
                    :is="detail.href ? 'a' : 'p'"
                    :href="detail.href || undefined"
                    class="text-gray-500 text-sm hover:text-primary transition-colors duration-200"
                  >
                    {{ detail.value }}
                  </component>
                  <p v-if="detail.sub" class="text-gray-400 text-xs mt-0.5">{{ detail.sub }}</p>
                </div>
              </div>
            </div>

            <!-- Office hours -->
            <div class="bg-primary/5 rounded-2xl p-5 mb-6 border border-primary/10">
              <h5 class="font-bold text-primary text-xs uppercase tracking-wider mb-3">Office Hours</h5>
              <div class="space-y-1.5 text-sm text-gray-600">
                <div class="flex justify-between">
                  <span>Monday - Friday</span>
                  <span class="font-semibold text-gray-900">8:00 AM - 6:00 PM</span>
                </div>
                <div class="flex justify-between">
                  <span>Saturday</span>
                  <span class="font-semibold text-gray-900">9:00 AM - 2:00 PM</span>
                </div>
                <div class="flex justify-between">
                  <span>Sunday</span>
                  <span class="font-semibold text-secondary-dark">Closed</span>
                </div>
              </div>
            </div>

            <!-- Social links -->
            <div class="mb-8">
              <h5 class="font-bold text-gray-700 text-xs uppercase tracking-wider mb-4">Follow Us</h5>
              <div class="flex items-center gap-3">
                <a
                  v-for="s in socials"
                  :key="s.href"
                  :href="s.href"
                  target="_blank"
                  rel="noopener"
                  :aria-label="s.label"
                  class="w-10 h-10 rounded-xl bg-primary/8 hover:bg-gradient-primary flex items-center justify-center text-primary hover:text-white transition-all duration-300"
                >
                  <i :class="['text-sm', s.icon]"></i>
                </a>
              </div>
            </div>

            <!-- Decorative quote card -->
            <div class="bg-gradient-hero rounded-2xl p-6 relative overflow-hidden">
              <div class="absolute inset-0 hex-pattern opacity-30 pointer-events-none"></div>
              <div class="relative">
                <i class="fa-solid fa-quote-left text-secondary/40 text-3xl mb-3"></i>
                <p class="text-white/75 text-sm italic leading-relaxed">
                  "We're committed to providing exceptional critical devices and services to enhance lives across Uganda - engineering with integrity, every step of the way."
                </p>
                <div class="mt-4 flex items-center gap-3">
                  <div class="w-8 h-0.5 bg-secondary rounded-full"></div>
                  <span class="text-secondary text-xs font-bold uppercase tracking-wider">Orel Medical Team</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

  </MainLayout>
</template>

<script setup>
import { ref, reactive } from 'vue'
import MainLayout from './Layout/MainLayout.vue'

const sending   = ref(false)
const submitted = ref(false)

const form = reactive({
  name:    '',
  email:   '',
  subject: '',
  phone:   '',
  service: '',
  message: '',
})

const submitForm = async () => {
  sending.value = true
  // Simulated send - integrate real backend or email service here
  await new Promise(r => setTimeout(r, 1200))
  sending.value   = false
  submitted.value = true
  Object.assign(form, { name: '', email: '', subject: '', phone: '', service: '', message: '' })
  setTimeout(() => { submitted.value = false }, 6000)
}

const serviceOptions = [
  'Medical Equipment Supply',
  'Trainings',
  'Tender Document Preparations',
  'Electrical Installations',
  'Biomedical Consultancy',
  'General Enquiry',
]

const locationSvg = '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>'
const phoneSvg    = '<path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.64A2 2 0 012 .17h3a2 2 0 012 1.72 12.05 12.05 0 00.72 2.92 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.05 12.05 0 002.92.72A2 2 0 0122 13.92v3z"/>'
const emailSvg    = '<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/>'

const contactInfo = [
  {
    svgPath: locationSvg,
    label:   'Our Location',
    value:   'Rubaga Road, Butikiro Complex, Kampala',
    sub:     '',
    href:    null,
    delay:   0,
  },
  {
    svgPath: phoneSvg,
    label:   'Call Us',
    value:   '+256 779 266 772',
    sub:     '+256 769 571 216',
    href:    'tel:+256779266772',
    delay:   100,
  },
  {
    svgPath: emailSvg,
    label:   'Email Us',
    value:   'orelmedicalsupplies@gmail.com',
    sub:     null,
    href:    'mailto:orelmedicalsupplies@gmail.com',
    delay:   200,
  },
]

const details = [
  {
    svgPath: locationSvg,
    label:   'Our Location',
    value:   'Rubaga Road, Butikiro Complex, Kampala',
    sub:     '',
    href:    null,
  },
  {
    svgPath: phoneSvg,
    label:   'Call Us',
    value:   '+256 779 266 772 / +256 769 571 216',
    sub:     null,
    href:    'tel:+256779266772',
  },
  {
    svgPath: emailSvg,
    label:   'Email Us',
    value:   'orelmedicalsupplies@gmail.com',
    sub:     null,
    href:    'mailto:orelmedicalsupplies@gmail.com',
  },
]

const socials = [
  { href: 'https://www.facebook.com/',   icon: 'fa-brands fa-facebook-f',  label: 'Facebook'  },
  { href: 'https://www.linkedin.com/',   icon: 'fa-brands fa-linkedin-in', label: 'LinkedIn'  },
  { href: 'https://twitter.com/',        icon: 'fa-brands fa-twitter',     label: 'Twitter'   },
  { href: 'https://www.instagram.com/', icon: 'fa-brands fa-instagram',   label: 'Instagram' },
]
</script>
